import React, {createRef} from 'react';
import ChatEventCodes from '../utils/ChatEventCodes';
import Message from './Message';

/**
 * Class to render the time slot / request a callback template.
 */
export default class TimeSlotTemplate extends Message {
    constructor(props) {
        super(props);

        this.selectTimeSlot = this.selectTimeSlot.bind(this);
        this.selectedTimeSlotTile = undefined;
        this.selectedTimeSlotPayload = undefined;
        this.submitSelection = this.submitSelection.bind(this);
        this.selectionErrorRef = createRef();
        this.maxNewSlots = 9;
        this.sortedTimeSlots = [];
        this.firstAvailableRef = createRef();
        this.submitSelectionFirstAvailable = this.submitSelectionFirstAvailable.bind(this);
        this.showMore = this.showMore.bind(this);
        this.showMoreRef = createRef();
        this.ariaLiveRef = createRef();

        this.state = {
            timeSlotIndex: 0,
            startingIndex: 0,
            timeSlots: [],
            /**@type {Array<{date: Date, payload: string}>} */
            displayedTimeSlots: []
        }
    }

    componentDidMount() {
        //set state displayed custom times to the max of 9
        this.setState({displayedTimeSlots: this.getTimeSelections()});
    }

    /**
     * Loops through the time slots provided in the props. If there are no timeslots, or we have reached
     * the end, return an empty array. If not, then add the max new slots against the current lookup
     * index. If that value is greater than the length of the full collection, then return what remains.
     * Else return from the current lookup index to the max number.
     * @returns The collection of new time slots to be shown.
     */
    getTimeSelections() {
        const customTimeslotLength = this.props.payload.customTimeSlot.length;
        if (customTimeslotLength === 0 || this.state.startingIndex >= customTimeslotLength) {
            return [];
        }

        let newMessages = [];

        if (this.state.startingIndex + this.maxNewSlots >= customTimeslotLength) {
            newMessages = this.props.payload.customTimeSlot.slice(this.state.startingIndex).map((timeslot) => {return {date: new Date(timeslot.payload * 1000), payload: timeslot.payload}});
        }
        else {
            newMessages = this.props.payload.customTimeSlot.slice(this.state.startingIndex, this.maxNewSlots + this.state.startingIndex).map((timeslot) => {return {date: new Date(timeslot.payload * 1000), payload: timeslot.payload}});
        }

        return newMessages;
    }

    /**
     * @returns The current date in a readab;e format.
     */
    getCurrentDate() {
        const date = new Date();
        return `${date.toLocaleDateString('en-us', {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}`
    }

    /**
     * Returns the current value if it is a string. If not, then attempts to convert it to a date.
     * If the date calculation is not a number, then return empty. Else return the readable
     * date string.
     * @param {String} currentValue The current "previous" timeslot value.
     * @returns 
     */
    static getMaskedHistory(currentValue) {
        if (typeof currentValue === 'string' || currentValue instanceof String) {
            return currentValue;
        }
        if (isNaN(currentValue * 1000)) {
            return '';
        }
        const date = new Date(currentValue * 1000);
        return `Call on ${date.toLocaleDateString('en-US')} at ${date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}`
    }

    /**
     * Determines if first avaialable is actually avaiable and then renders
     * the button block for that selection.
     * @returns The HTML block for the first available button.
     */
    renderFirstAvailableBlock() {
        if (!this.props.payload.isFirstAvailable) {
            return '';
        }

        return <div className='time-slot__first-available'>
                    <div className='time-slot__first-available-heading'>
                        {this.props.payload.FirstAvailable[0].subText}
                    </div>
                    <button className='first-available-button button--green'
                    onClick={() => this.submitSelectionFirstAvailable(this.props.payload.FirstAvailable[0].payload)}
                    title='Request the first available callback available from an agent.'
                    ref={this.firstAvailableRef}>
                        {this.props.payload.FirstAvailable[0].title}
                    </button>
                </div>
    }

    /**
     * Renders the show more link/button if there are additional timeslots that
     * have been returned from the callback service.
     */
    renderShowMoreButton() {
        if (this.state.startingIndex === this.props.payload.customTimeSlot.length || this.state.startingIndex + this.maxNewSlots >= this.props.payload.customTimeSlot.length) {
            return '';
        }

        return <a href='#' role='button' className='show-more-button' onClick={(e) => this.showMore(e)} ref={this.showMoreRef}>See More Times</a>
    }

    /**
     * Builds the time slot blocks to be rendered. First loops through displayedTimeSlots
     * form the current state and creates a new array based on the day grouping. Once that
     * loop is done, either by the next day being hit or reaching the end of the collection,
     * then the new collection is pushed to an array. That array is then looped through
     * and creates the collection of HTML blocks that display day block renderings of
     * available timeslots.
     * @returns The HTML block collection of the timeslots to display to the user.
     */
    renderTimeSlotSelections() {
        if (!this.state.displayedTimeSlots || this.state.displayedTimeSlots.length === 0) {
            return '';
        }

        /**@type {Array<Array<{date: Date, payload: string}>>} */
        const sortedTimeSlots = [];
        let blockDate = this.state.displayedTimeSlots[0].date.getDate();
        let sortedDays = [];

        for (let i = 0; i < this.state.displayedTimeSlots.length; i++) {
            let currentDate = this.state.displayedTimeSlots[i].date.getDate();

            if (currentDate === blockDate) {
                sortedDays.push(this.state.displayedTimeSlots[i]);
            }

            /**
             * If the dates now mismatch, push the sortedDays array, clear it, push the current object
             * which is the first slot of the next day for sortedDays, and update the blockDate.
             *
             * If it is the last item in the collection, just push what will be the last timeslot
             * of the last date displayed.
             */
            if (blockDate !== currentDate) {
                sortedTimeSlots.push(sortedDays);
                sortedDays = [];
                sortedDays.push(this.state.displayedTimeSlots[i]);
                blockDate = currentDate;
            }

            if (i === this.state.displayedTimeSlots.length - 1) {
                sortedTimeSlots.push(sortedDays);
            }
        }
        const timeslotHtmlBlocks = [];
        for (const dayArray of sortedTimeSlots) {
            timeslotHtmlBlocks.push(
                <div className='time-slot__day-block'>
                    <div className='day-block__date'>{dayArray[0].date.toLocaleDateString('en-us', {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})}</div>
                    <div className='day-block__items'>
                        {dayArray.map((timeSlotItem) => {
                            return <button className='day-block__item'
                                    key={timeSlotItem}
                                    onClick={(e) => {this.selectTimeSlot(e, timeSlotItem)}}
                                    title={`Select the call back time slot of ${timeSlotItem.title}`}>
                                        {timeSlotItem.date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}
                                    </button>
                        })}
                    </div>
                </div>
            )
        }

        return timeslotHtmlBlocks;
    }

    /**
     * Controls the selection of a timeslot. This is only for the collection of
     * slots that come through as 15 minute increments and not the first available.
     * @param {PointerEvent} e The pointer event determing the HTML interaction target.
     * @param {Object} timeSlotItem The time slot object that was clicked on for payload access.
     */
    selectTimeSlot(e, timeSlotItem) {
        if (this.selectedTimeSlotTile) {
            this.selectedTimeSlotTile.classList.remove('selected');
        }
        this.selectedTimeSlotTile = e.target;
        this.selectedTimeSlotTile.classList.add('selected');
        this.selectedTimeSlotPayload = timeSlotItem;
        this.selectionErrorRef.current.classList.add('hidden');
    }

    /**
     * Handle the click on the show more link to display more available timeslots.
     * This also updates a hidden aria-live live region in the template that informs the
     * user how many more timesplots have been shown.
     * @param {Event} e
     */
    showMore(e) {
        e.preventDefault();
        const nextIndex = this.state.startingIndex + this.maxNewSlots;
        this.setState({startingIndex: nextIndex}, () => {
            const newTimeSlots = this.getTimeSelections();
            const updatedDisplayedTimeSlots = this.state.displayedTimeSlots.concat(newTimeSlots);
            if (this.ariaLiveRef.current) {
                this.ariaLiveRef.current.innerText = newTimeSlots.length === 1 ? `${newTimeSlots.length} more time slot selection displayed.` : `${newTimeSlots.length} more time slot selections displayed.`;
            }
            this.setState({displayedTimeSlots: updatedDisplayedTimeSlots});
        });
    }

    /**
     * The selected payload to be sent back to the bot. This can either be the selected
     * time slot or "ASAP" for the first available button.
     * @param {{date: Date, payload: string}} timeslot The payload to be sent back to the bot.
     */
    submitSelection(timeslot) {
        if (!timeslot || !timeslot.date || !timeslot.payload) {
            this.selectionErrorRef.current.classList.remove('hidden');
        }
        else {
            this.setState({userInteracted: true}, () => {
                document.dispatchEvent(new MessageEvent(ChatEventCodes.UserMaskedInput, {
                    data: {
                        botPayload: timeslot.payload,
                        displayMessage: `Call on ${timeslot.date.toLocaleDateString('en-US')} at ${timeslot.date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'})}`
                    }
                }));
            });
        }
    }

    /**
     * Submits the user's request to get a call back at the first available time. This will be
     * a string only and generally will be something such as "ASAP".
     * @param {String}} payload The string representing the first available button selection.
     */
    submitSelectionFirstAvailable(payload) {
        this.setState({userInteracted: true}, () => {
            document.dispatchEvent(new MessageEvent(ChatEventCodes.UserMaskedInput, {
                data: {
                    botPayload: payload,
                    displayMessage: payload
                }
            }));
        });
    }

    render() {
        if (this.props.message.isChatHistory || this.state.userInteracted) {
            return (
                <div className={this.generateSenderClassName('time-slot')}>
                    {this.generateSenderMessage()}
                    <div className='time-slot__text message-text'>
                        <div className='time-slot__heading'>
                            {this.props.payload.Heading}
                        </div>
                    </div>
                </div>
            )
        }
        return (
            <div className={this.generateSenderClassName('time-slot')}>
                {this.generateSenderMessage()}
                <div className='time-slot__text message-text'>
                    <div className='time-slot__heading'>
                        {this.props.payload.Heading}
                    </div>
                    <div className='time-slot__wrapper'>
                        {this.renderFirstAvailableBlock()}
                        <div className='time-slot__custom-time-text' dangerouslySetInnerHTML={this.evaluateMarkdown(this.props.payload.customTimeText)} />
                        <div className='time-slot__selection-error hidden' aria-live='assertive' ref={this.selectionErrorRef}>
                            Select a time slot.
                        </div>
                        <div className='time-slot__time-slot-items'>
                            {this.renderTimeSlotSelections()}
                            {this.renderShowMoreButton()}
                        </div>
                    </div>
                    <button className='time-slot__submit-button button--black'
                            onClick={() => {this.submitSelection(this.selectedTimeSlotPayload)}}
                            title='Send your selected call back time slot to the virtual assistant.'>
                        {this.props.payload.button.label}
                    </button>
                </div>
                <div className='vh' aria-live='polite' ref={this.ariaLiveRef}></div>
            </div>
        );
    }
}